/**
 * Port of lib/trait_chat.py.
 *
 * Conversational appearance/temperament capture.
 *
 * Rigid dropdowns are a poor fit for traits like "intense" or "wheatish for their community" — they are
 * hard to self-classify. Instead the user simply DESCRIBES the person in a chat, and the AI maps that
 * description onto the SAME structured trait options the dropdowns used.
 *
 * Critical boundary: the AI does pure EXTRACTION (natural language -> structured option keys). It never
 * scores, never names a rising sign, and never touches candidate ranking — `rising_sign_from_traits`
 * and the deterministic engine still do all the astrology on the structured output. So the conversation
 * only replaces the INPUT method; it cannot bias the result.
 *
 * trait_chat_turn is async (openai_service is fetch-based). Python tests monkeypatched
 * lib.trait_chat.call_openai_conversation_json; the TS seam is set_call_openai_conversation_json_impl.
 */

import { load_instruction, load_schema } from "./instructions";
import { call_openai_conversation_json, call_openai_conversation_json_streaming } from "./openai_service";
import { todayLocal, formatISODate } from "./datetime";
import { pythonRound } from "./pyutil";
import { SIGN_NAMES, normalize_trait_answers, trait_questions_for_age, type TraitQuestion, type TraitTarget } from "./traits";

/**
 * Age-gated questions, optionally scoped to ONE target: "lagna" (appearance) or "moon"
 * (personality). The Discuss Appearance and Discuss Personality chats each pass their target so
 * the catalog/schema/instructions only cover their own dimensions.
 */
function _target_questions(age_years: number | null, target: TraitTarget | null): TraitQuestion[] {
  const qs = trait_questions_for_age(age_years);
  return target ? qs.filter((q) => q.target === target) : qs;
}

// Test seam replacing Python's monkeypatch of the module-level import.
type ConversationCall = typeof call_openai_conversation_json;
let _call_openai_conversation_json: ConversationCall = call_openai_conversation_json;

export function set_call_openai_conversation_json_impl(impl: ConversationCall | null): void {
  _call_openai_conversation_json = impl ?? call_openai_conversation_json;
}

/**
 * The questions and their valid option keys/labels, handed to the model so it maps onto exactly
 * the options the scoring engine understands — age-gated, so a child is never asked about
 * adult-pattern markers (balding) or growth-stage height/build.
 */
export function _option_catalog(age_years: number | null = null, target: TraitTarget | null = null): Record<string, any>[] {
  return _target_questions(age_years, target).map((q) => ({
    key: q.key,
    asks_about: q.prompt,
    options: Object.fromEntries(Object.entries(q.options).map(([key, opt]) => [key, opt.label])),
  }));
}

/**
 * Strict JSON schema: a conversational reply, the FULL traits object (every question key, each
 * an array of valid option keys), the AI's own rising-sign likelihood estimate, and a done flag.
 */
export function _trait_chat_schema(age_years: number | null = null, target: TraitTarget | null = null): Record<string, any> {
  const trait_props: Record<string, any> = {};
  for (const q of _target_questions(age_years, target)) {
    trait_props[q.key] = {
      type: "array",
      items: { type: "string", enum: Object.keys(q.options) },
    };
  }
  // Each split chat reads its OWN editable schema (kept in lockstep with its prompt's basename):
  // personality → trait_personality.json, appearance → trait_appearance.json. The age-gated trait
  // options and the sign list are injected so the editable JSON never drifts.
  const schemaFile = target === "moon" ? "trait_personality.json" : "trait_appearance.json";
  return load_schema(schemaFile, { trait_options: trait_props, trait_keys: Object.keys(trait_props), sign_names: [...SIGN_NAMES] });
}

/**
 * A human-readable list of the categories that already have values (from the photo or
 * earlier turns). Embedded in the per-turn instructions so 'don't re-ask' stays in force
 * on EVERY turn — a single mention in the seed message is too weak: models drift back to
 * their own question plan and re-ask what a photo already answered.
 */
export function _known_traits_block(
  known: Record<string, string[]>,
  age_years: number | null,
  rejected: Record<string, string[]> | null = null,
): string {
  const by_key: Record<string, any> = {};
  for (const q of trait_questions_for_age(age_years)) by_key[q.key] = q;
  const label_lines = (source: Record<string, string[]>): string[] => {
    const lines: string[] = [];
    for (const [key, opts] of Object.entries(source)) {
      const question = by_key[key];
      if (!question) continue;
      const labels = opts.map((o: string) => question.options[o]?.label ?? o);
      lines.push(`- ${key}: ${labels.join(", ")}`);
    }
    return lines;
  };
  const lines = label_lines(known);
  const rejected_lines = label_lines(rejected ?? {});
  let block = "";
  if (lines.length > 0) {
    block +=
      "\n\nALREADY ANSWERED (from a photo or earlier in the conversation) — these categories are " +
      "FILLED:\n" +
      lines.join("\n") +
      "\n" +
      "NEVER ask about an already-answered category again, and never ask the user to confirm or " +
      "refine it — treat it as settled fact. Include its current keys in `traits` every turn. " +
      "Only change one if the USER spontaneously corrects it. Ask only about categories that are " +
      "still empty; if every category is filled, set done=true.";
  }
  if (rejected_lines.length > 0) {
    block +=
      "\n\nALREADY REJECTED — the person DEFINITELY DOES NOT have these (settled facts; never " +
      "re-ask, never suggest them as answer choices, and echo their keys in `rejected_traits` " +
      "every turn):\n" +
      rejected_lines.join("\n");
  }
  return block;
}

/**
 * System guidance for the chat, loaded from instructions/trait_chat.md and completed with
 * the runtime option catalog, sign list, and the already-answered categories. Re-sent each turn
 * (conversation history is server-side), so the don't-re-ask rule never falls out of context.
 */
export function _instructions(
  age_years: number | null,
  known: Record<string, string[]> | null = null,
  rejected: Record<string, string[]> | null = null,
  target: TraitTarget | null = null,
): string {
  const age_note =
    age_years !== null && age_years !== undefined
      ? `\n\nThe person being described is about ${pythonRound(age_years, 0).toFixed(0)} years old — frame every question ` +
        "for that age group and judge all features against their age peers."
      : "";
  // Each split chat has its OWN editable system prompt: personality → trait_personality.md,
  // appearance → trait_appearance.md. Both carry {age}/{as_of_date} placeholders (in their
  // objectivity / age-awareness blocks); the trait chat is the only thing that loads them, so we
  // fill them here.
  const promptFile = target === "moon" ? "trait_personality.md" : "trait_appearance.md";
  const age_text = age_years !== null && age_years !== undefined ? pythonRound(age_years, 0).toFixed(0) : "unknown age";
  const base = load_instruction(promptFile).split("{age}").join(age_text).split("{as_of_date}").join(formatISODate(todayLocal()));
  return (
    base +
    "\n\nVALID TRAIT OPTIONS (use only these exact keys):\n" +
    JSON.stringify(_option_catalog(age_years, target)) +
    "\n\nAVAILABLE RISING SIGNS: " +
    SIGN_NAMES.join(", ") +
    age_note +
    _known_traits_block(known ?? {}, age_years, rejected)
  );
}

/**
 * One turn of the person's OpenAI conversation (server keeps the history — we don't resend it).
 * Pass conversation_id=null with an empty user_message to create the conversation and get the
 * opening question; if `current_traits` are supplied there, the fresh conversation is SEEDED with
 * them so it continues from what's already known instead of re-asking. Returns a dict: reply,
 * choices, traits, likelihood, done, conversation_id, error. age_years gates the option catalog
 * and tells the model the real age group.
 *
 * Already-known traits (e.g. from a photo) ride along in the INSTRUCTIONS on every turn as
 * settled facts the model must not re-ask, and the returned traits are MERGED over them —
 * the model can correct a category, but forgetting to echo one can never erase it.
 */
export async function trait_chat_turn(
  conversation_id: string | null,
  user_message: string,
  current_traits: Record<string, any> | null = null,
  run_data: Record<string, any> | null = null,
  log_slug: any = null,
  age_years: number | null = null,
  current_rejected: Record<string, any> | null = null,
  target: TraitTarget | null = null,
  on_reply_delta: ((partial_reply: string) => void) | null = null,
): Promise<Record<string, any>> {
  // Only this target's questions are in scope, so known/echoed traits stay within the chat's lane
  // and the other chat's saved traits are never touched.
  const inScope = new Set(_target_questions(age_years, target).map((q) => q.key));
  const known: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(normalize_trait_answers(current_traits ?? {}, age_years))) {
    if (v && v.length && (target === null || inScope.has(k))) known[k] = v;
  }
  const known_rejected: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(normalize_trait_answers(current_rejected ?? {}, age_years))) {
    if (v && v.length && (target === null || inScope.has(k))) known_rejected[k] = v;
  }
  let user_input: string;
  if (user_message && user_message.trim()) {
    user_input = user_message.trim();
  } else if (Object.keys(known).length > 0 || Object.keys(known_rejected).length > 0) {
    user_input =
      "Continue describing the SAME person. The ALREADY ANSWERED and ALREADY REJECTED " +
      "categories in your instructions are settled — do not re-ask or re-confirm them. Briefly " +
      "note what's known, then ask up to 3 questions about what is still empty (including what " +
      "they definitely do NOT have).";
  } else {
    user_input = "Start the description — ask me your first questions.";
  }
  let parsed: Record<string, any> | null;
  let error: string | null;
  const schema = _trait_chat_schema(age_years, target);
  const instructions = _instructions(age_years, known, known_rejected, target);
  // When the UI passes a delta callback, stream the reply token-by-token (ChatGPT-style). Tests
  // and the non-UI path keep the single-shot seam (`set_call_openai_conversation_json_impl`).
  if (on_reply_delta) {
    [parsed, conversation_id, error] = await call_openai_conversation_json_streaming(
      "trait_chat",
      schema,
      instructions,
      conversation_id,
      user_input,
      on_reply_delta,
      log_slug,
      run_data,
    );
  } else {
    [parsed, conversation_id, error] = await _call_openai_conversation_json(
      "trait_chat",
      schema,
      instructions,
      conversation_id,
      user_input,
      log_slug,
      run_data,
    );
  }
  if (error || !parsed) {
    return {
      reply: null,
      traits: null,
      rejected: null,
      likelihood: {},
      done: false,
      conversation_id,
      error: error || "No response from the assistant.",
    };
  }
  const likelihood: Record<string, number> = {};
  for (const item of parsed["rising_sign_likelihood"] || []) {
    const sign = item?.["sign"];
    if (SIGN_NAMES.includes(sign)) {
      const score = Number(item?.["score"]);
      if (Number.isNaN(score) || item?.["score"] === null || item?.["score"] === undefined) continue;
      likelihood[sign] = Math.max(0.0, Math.min(100.0, score));
    }
  }
  // Merge, never replace: start from what is already established and let the model's
  // NON-EMPTY categories win (so a user correction sticks). The schema forces every key
  // with empty-array-means-unknown, so a model that forgets to echo the photo's face
  // shape returns an empty array — which must read as "no news", not "delete it".
  // Before this merge, a single forgetful turn silently erased photo traits, and the
  // chat then (from its view, correctly) re-asked what the photo had already answered.
  const merged: Record<string, string[]> = { ...known };
  for (const [key, opts] of Object.entries(normalize_trait_answers(parsed["traits"] || {}, age_years))) {
    merged[key] = opts;
  }
  // Rejected traits merge the same way (empty array = "no news", never a deletion).
  const merged_rejected: Record<string, string[]> = { ...known_rejected };
  for (const [key, opts] of Object.entries(normalize_trait_answers(parsed["rejected_traits"] || {}, age_years))) {
    merged_rejected[key] = opts;
  }
  // An option can't be both present and definitely absent. A DEFINITE absence is the harder
  // statement (it's an absolute gate), so when the model returns the same option in both maps
  // the rejection wins and the positive copy is dropped.
  for (const [key, rejected_opts] of Object.entries(merged_rejected)) {
    if (!rejected_opts.length || !merged[key]) continue;
    const rej = new Set(rejected_opts);
    merged[key] = merged[key].filter((opt) => !rej.has(opt));
    if (merged[key].length === 0) delete merged[key];
  }
  // Per-trait influence preset (astrological relevance) → {key: {option: level}}, kept only for
  // valid in-scope keys/options that the model actually asserted.
  const LEVELS = new Set(["minimal", "low", "medium", "high"]);
  const valid_options: Record<string, Set<string>> = {};
  for (const q of _target_questions(age_years, target)) valid_options[q.key] = new Set(Object.keys(q.options));
  const influence: Record<string, Record<string, string>> = {};
  for (const item of parsed["influence"] || []) {
    const key = item?.["trait"];
    const opt = item?.["option"];
    const level = item?.["level"];
    if (valid_options[key]?.has(opt) && LEVELS.has(level) && (merged[key]?.includes(opt) ?? false)) {
      (influence[key] ??= {})[opt] = level;
    }
  }
  return {
    reply: String(parsed["reply"] || "").trim(),
    traits: merged,
    rejected: merged_rejected,
    influence,
    likelihood,
    done: Boolean(parsed["done"]),
    conversation_id,
    error: null,
  };
}
